import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { Menu } from "@headlessui/react";
import { usePaymentCreditNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentCreditNoteListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import CreditNoteCretionModal from "../CreditNoteCretionModal";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { BsThreeDots } from "react-icons/bs";
import RBACFlag from "@/app/components/core/RBACFlag";

const CreditNoteListComponent = () => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat } = useUtils();
  const { NO_CART } = useImageConstants();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { creditNoteList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;

  const [open, setOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);

  const { execute: fetchCreditNote } = usePaymentCreditNoteListAPI();
  useEffect(() => {
    if (customerId) {
      fetchCreditNote(customerId, 1, 10);
    }
  }, [customerId]);

  const isTransactionLoading = useLoader("isCustomerCreditNoteTableInProgress");

  return (
    <div className="h-full">
      <div className="pb-0.5 rounded-lg bg-white pl-2 pr-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pb-1 gap-2 mb-2">
          <p className="text-sm font-semibold mt-3">Credit Note</p>
        </div>
      </div>
      {isTransactionLoading ? (
        <TableSkeleton />
      ) : creditNoteList?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {tableHeader.map((key, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
                      >
                        {key.item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {creditNoteList?.map((cl, index) => {
                    const isFirst = index === 0;
                    const isLast = index === creditNoteList.length - 1;
                    const isSingle = creditNoteList.length === 1;

                    return (
                      <tr
                        key={index}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {cl?.credit_note_receipt_id}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{cl?.invoice_id}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          {cl?.created_at ? veticDateFormat(cl?.created_at) : "NA"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{cl?.credit_amount}</td>
                         <td className="px-2 py-3 text-xs text-black max-w-[220px] sm:max-w-[340px] whitespace-normal break-words">
                          {cl?.comments || "NA"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-gray-500">{cl?.created_by}</td>
                       
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black text-center
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <RBACFlag actionId="CREDIT_NOTE_ACTION_CTA">
                            <Menu as="div" className="relative inline-block text-left">
                              <div>
                                <Menu.Button className="flex items-center justify-center w-full">
                                  <BsThreeDots className="w-4 h-4" />
                                </Menu.Button>
                              </div>

                              <Menu.Items className="absolute right-6 z-10 -mt-7 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transition-transform duration-100 ease-out">
                                <div className="py-1">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <a
                                        href="#"
                                        className={`block px-4 py-2 text-xs ${
                                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                                        }`}
                                        onClick={() => {
                                          setOpen(true);
                                          setInvoiceId(cl?.credit_note_receipt_id);
                                        }}
                                      >
                                        Raise Debit Note Request
                                      </a>
                                    )}
                                  </Menu.Item>
                                </div>
                              </Menu.Items>
                            </Menu>
                          </RBACFlag>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {open && <CreditNoteCretionModal creditNoteId={invoiceId} open={open} setOpen={setOpen} />}
        </div>
      ) : (
        creditNoteList?.length === 0 && (
          <div className="h-[500px] bg-white">
            <NoItemFoundComponent
              message=""
              description="No Credit Note Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
    </div>
  );
};

export default CreditNoteListComponent;
const tableHeader = [
  { item: "Credit Note ID" },
  { item: "Invoice ID" },
  { item: "Date & Time" },
  { item: "Amount" },
  { item: "Comment" },
  { item: "Created By" },
  { item: "Action" },
];
const TableSkeleton = () => {
  return (
    <div className="mt-3 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto animate-pulse">
      <div className="w-full">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
              <tr>
                {[...Array(7)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(6)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                  <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-300 text-center">
                    <div className="h-6 bg-gray-200 rounded w-8 mx-auto"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
